/* eslint-disable */
import { useState, useEffect, useMemo } from "react";
import { OLAPTransformerResult, useParameter, useDictionary, Column } from "../../../shared";
import Highcharts, { SeriesOptionsType } from "highcharts";
import { useChartOptions } from "../../../../charts/useChartOptions";
import { legendPipe, chartPipe, tooltipPipe, addXAxisPipe, addYAxisPipe } from "../../../../charts/pipes";
import { dictionaryService } from "../../../dataFlow";
import { arrayByLength, uniq } from "../../../../../shared";
import { last } from "lodash";
import { SLIDE_NUMBER } from "../constants";

const CATEGORIES_AXIS = "countries.default";
const SERIES_AXIS = "calendar.default";
const CALENDAR_STEP = 10000;
const cacheCountries = new Map(); // TODO: Костыль

export function useHighchartTransformer(olapDataChart: OLAPTransformerResult | null, olapDataTable: OLAPTransformerResult | null, setHighlight: (selection: Record<string, number> | null) => void): Highcharts.Options | undefined {
  const [state, setState] = useState<Highcharts.Options>();
  const [countries] = useParameter<number[]>('@countries');
  const [calendarYear] = useParameter<number>('@calendar');
  const [commodity] = useParameter<number>('@commodities');
  const [businessUnit] = useParameter<number>('@business-units');
  const calendar = useMemo(() => [-5, -4, -3, -2, -1, 0].map(x => (calendarYear || 0) + (CALENDAR_STEP * x)), [calendarYear]);

  const [countriesDisplayItems] = useDictionary(dictionaryService, "slides-countries-display", undefined, {
    slide: SLIDE_NUMBER,
    olap_commodity: commodity,
    business_unit: businessUnit,
  });

  const categories = useMemo(
    () => uniq(countries?.map(countryId => {
      const column = olapDataChart?.columns.find(col => col.selection[CATEGORIES_AXIS] === countryId);
      const colHeader = column?.headers?.find(header => header.id === `${CATEGORIES_AXIS}.name`);
      let localeName = colHeader?.name as any;
      if (localeName === undefined) {
        const displayItem = countriesDisplayItems?.find(item => item.getValue<number>("olap_country") === countryId);
        localeName = displayItem?.getValue<any>('name');
      }
      if (typeof localeName === "object") {
        localeName = localeName[9];
      }
      if (localeName) {
        cacheCountries.set(countryId, localeName);
      } else {
        localeName = cacheCountries.get(countryId);
      }
      return localeName || '';
    }) || []),
    [olapDataChart, countries]
  );

  const options = useChartOptions({
    plotOptions: {
      series: {
        point: {
          events: {
            mouseOver: function () {
              const { selection } = this as any;
              setHighlight(selection);
            }
          }
        },
        events: {
          mouseOut: function () {
            setHighlight(null);
          }
        }
      }
    },
    tooltip: {
      useHTML: true,
      formatter: function () {
        const year = this.point.series.name;
        const unit = 'USD/MT';
        const tableUnit = 'KG/Person';
        const country = this.point.category;
        const pointValue = Highcharts.numberFormat(this.point.y || 0, 0);
        const tableValue = Highcharts.numberFormat(this.point['tableData'] || 0, 2);
        return `<div>
            <b>${country} - ${year}</b><br/>
            <b>Producer price</b>: <span>${pointValue} ${unit}</span><br/>
            <b>Consumption per capita</b>: <span>${tableValue} ${tableUnit}</span>
        </div>`;
      },
    },
  }, [
    chartPipe("column"),
    legendPipe("bottom"),
    addXAxisPipe(categories),
    addYAxisPipe({
      labels: {
        formatter: function () {
          var label = this.axis.defaultLabelFormatter.call(this);
          if (/^[0-9]{4}$/.test(label)) {
            return Highcharts.numberFormat(this.value, 0);
          }
          return label;
        },
      },
    }),
  ]);

  useEffect(() => {
    if (olapDataChart && olapDataTable) {
      const series: SeriesOptionsType[] = calendar?.map((calendarYear) => {
        const isFirstSerie = calendar[0] === calendarYear;
        const isLastSerie = calendar[calendar.length - 1] === calendarYear;
        const serieRow = olapDataChart?.rows.find(row => Number(row.selection[SERIES_AXIS]) === calendarYear);
        const tableRow = olapDataTable?.rows.find(row => Number(row.selection[SERIES_AXIS]) === calendarYear);
        const serieName = calendarYear.toString().slice(1, 5);
        const seriePoints = countries?.map((countryId, countryIndex) => {
          const countryColumn = olapDataChart?.columns.find(col => Number(col.selection[CATEGORIES_AXIS]) === countryId);
          const point = serieRow?.points.find(point => point.column.selection[CATEGORIES_AXIS] === countryColumn?.selection[CATEGORIES_AXIS]);
          const tablePoint = tableRow?.points.find(point => point.column.selection[CATEGORIES_AXIS] === countryColumn?.selection[CATEGORIES_AXIS]);

          const everyCategoryPointsIsEmpty = olapDataChart?.rows.every(row => {
            const currentCountryPoint = row.points.find(p => p.column.selection[CATEGORIES_AXIS] === countryColumn?.selection[CATEGORIES_AXIS]);
            return !Boolean(currentCountryPoint?.value);
          });
          const value = point?.value || 0;
          const tableData = tablePoint?.value || 0;
          const selection = {
            [SERIES_AXIS]: calendarYear,
            [CATEGORIES_AXIS]: countryId,
          };
          return { x: countryIndex, y: value, selection, tableData, empty: everyCategoryPointsIsEmpty };
        });

        return {
          type: "column",
          yAxis: 0,
          showInLegend: true,
          dataLabels: {
            enabled: isFirstSerie || isLastSerie,
            formatter: function () {
              const { y: val, point } = this as any;
              if (point.empty) {
                return '';
              }
              return Highcharts.numberFormat(val || 0, 0);
            },
          },
          name: serieName,
          id: calendarYear.toString(),
          data: seriePoints,
        } as SeriesOptionsType;
      });
      const result: Highcharts.Options = {
        ...options,
        series: series,
      };

      setState(result);
    } else {
      setState(undefined);
    }
  }, [olapDataChart, olapDataTable, countries, calendar]);
  return state;
}
